import { Component, Input, OnChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Education } from '../../interface/user-profile';
import { UserProfileService } from '../../service/user-profile.service';

@Component({
  selector: 'app-profile-education',
  templateUrl: './profile-education.component.html',
  styleUrls: ['./profile-education.component.css'],
})
export class ProfileEducationComponent implements OnChanges {
  @Input() education: Education[] | null = [];
  @Input() mode: 'view' | 'edit' = 'view';
  isModalOpen = false;
  isEditing = false;
  editingIndex = -1;
  isSaving = false;
  eduForm!: FormGroup;

  // Color palette for education icons (4 colors, randomly assigned)
  private colorPalette = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981'];

  constructor(
    private fb: FormBuilder,
    private userProfileService: UserProfileService,
  ) {}

  ngOnChanges(): void {}

  /**
   * Get a random color from the palette
   */
  private getRandomColor(): string {
    return this.colorPalette[
      Math.floor(Math.random() * this.colorPalette.length)
    ];
  }

  initForm(edu?: Education): void {
    // Keep existing color if editing, or assign random color if creating new
    const defaultColor = edu?.color || this.getRandomColor();

    this.eduForm = this.fb.group({
      university: [edu?.university || '', Validators.required],
      degree: [edu?.degree || '', Validators.required],
      major: [edu?.major || '', Validators.required],
      grade: [edu?.grade || ''],
      startDate: [edu?.startDate || '', Validators.required],
      endDate: [edu?.endDate || ''],
      details: [edu?.details || ''],
      isCurrent: [edu?.isCurrent ?? false],
    });

    // toggle endDate requirement based on isCurrent checkbox
    this.eduForm.get('isCurrent')?.valueChanges.subscribe((isCurrent) => {
      if (isCurrent) {
        this.eduForm.get('endDate')?.setValue('');
        this.eduForm.get('endDate')?.clearValidators();
      } else {
        this.eduForm.get('endDate')?.setValidators(Validators.required);
      }
      this.eduForm.get('endDate')?.updateValueAndValidity();
    });
  }

  openAddModal(): void {
    this.isEditing = false;
    this.editingIndex = -1;
    this.initForm();
    this.isModalOpen = true;
  }

  openEditModal(edu: Education, index: number): void {
    this.isEditing = true;
    this.editingIndex = index;
    this.initForm(edu);
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }

  getPeriod(edu: Education): string {
    const start = this.formatDate(edu.startDate);
    const end = edu.endDate ? this.formatDate(edu.endDate) : 'Present';
    return `${start} – ${end}`;
  }

  saveEducation(): void {
    if (!this.eduForm.valid) {
      this.eduForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const formValue = this.eduForm.value;

    // Build updated list
    const updatedList: Education[] = [...(this.education || [])];
    const newEdu: Education = {
      university: formValue.university,
      degree: formValue.degree,
      major: formValue.major,
      grade: formValue.grade || null,
      startDate: formValue.startDate,
      endDate: formValue.isCurrent ? null : formValue.endDate,
      details: formValue.details || null,
      isCurrent: formValue.isCurrent,
    };

    if (this.isEditing && this.editingIndex >= 0) {
      updatedList[this.editingIndex] = newEdu;
    } else {
      updatedList.push(newEdu);
    }

    const formData = new FormData();
    formData.append('educations', JSON.stringify(updatedList));

    this.userProfileService.updateUserProfile(formData).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.education = response.educations ?? updatedList;
        this.closeModal();
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error updating education:', error);
        alert('Failed to update. Please try again.');
      },
    });
  }

  deleteEducation(index: number): void {
    if (!confirm('Remove this education?')) return;

    const updatedList = (this.education || []).filter((_, i) => i !== index);
    const formData = new FormData();
    formData.append('educations', JSON.stringify(updatedList));

    this.userProfileService.updateUserProfile(formData).subscribe({
      next: (response) => {
        this.education = response.educations ?? updatedList;
      },
      error: (error) => {
        console.error('Error deleting education:', error);
        alert('Failed to delete. Please try again.');
      },
    });
  }

  get isCurrent(): boolean {
    return this.eduForm?.get('isCurrent')?.value;
  }
}
